import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { fetchNotifications, markNotificationAsRead, getUnreadCount, markAllNotificationsAsRead } from './notificationsService';

const initialState = {
    notifications: [],
    unreadCount: 0,
    lastDoc: null,
    hasMore: true,
    status: 'idle',
    error: null
};

export const fetchNotificationsThunk = createAsyncThunk(
    'notifications/fetchNotifications',
    async ({ employeeId, lastDoc, showMore = false }, { rejectWithValue }) => {
        try {
            const result = await fetchNotifications(employeeId, showMore ? lastDoc : null);
            return { ...result, showMore };
        } catch (error) {
            return rejectWithValue(error.message);
        }
    }
);

export const markNotificationAsReadThunk = createAsyncThunk(
    'notifications/markAsRead',
    async (notificationId, { rejectWithValue }) => {
        try {
            await markNotificationAsRead(notificationId);
            return notificationId;
        } catch (error) {
            return rejectWithValue(error.message);
        }
    }
);

export const getUnreadCountThunk = createAsyncThunk(
    'notifications/getUnreadCount',
    async (employeeId, { rejectWithValue }) => {
        try {
            const count = await getUnreadCount(employeeId);
            return count;
        } catch (error) {
            return rejectWithValue(error.message);
        }
    }
);

export const markAllNotificationsAsReadThunk = createAsyncThunk(
    'notifications/markAllAsRead',
    async (employeeId, { rejectWithValue, dispatch }) => {
        try {
            const result = await markAllNotificationsAsRead(employeeId);

            // Refresh the unread count after marking all as read
            dispatch(getUnreadCountThunk(employeeId));

            return result;
        } catch (error) {
            console.error('Error in markAllNotificationsAsReadThunk:', error);
            return rejectWithValue(error.message);
        }
    }
);

const notificationsSlice = createSlice({
    name: 'notifications',
    initialState,
    reducers: {
        clearNotifications: (state) => {
            state.notifications = [];
            state.lastDoc = null;
            state.hasMore = true;
        },
    },
    extraReducers: (builder) => {
        builder
            // Fetch notifications
            .addCase(fetchNotificationsThunk.pending, (state) => {
                state.status = 'loading';
            })
            .addCase(fetchNotificationsThunk.fulfilled, (state, action) => {
                state.status = 'succeeded';
                // If we're loading more (have lastDoc AND showMore flag is true), append to existing notifications
                if (state.lastDoc && action.payload.showMore) {
                    // Deduplicate notifications by id when appending
                    const existingIds = new Set(state.notifications.map(n => n.id));
                    const newNotifications = action.payload.notifications.filter(n => !existingIds.has(n.id));
                    state.notifications = [...state.notifications, ...newNotifications];
                } else {
                    // Otherwise, replace existing notifications
                    state.notifications = action.payload.notifications;
                }

                // Always update lastDoc and hasMore
                state.lastDoc = action.payload.lastDoc;
                state.hasMore = action.payload.hasMore;
            })
            .addCase(fetchNotificationsThunk.rejected, (state, action) => {
                state.status = 'failed';
                state.error = action.payload;
            })
            // Mark as read
            .addCase(markNotificationAsReadThunk.fulfilled, (state, action) => {
                const notification = state.notifications.find(n => n.id === action.payload);
                if (notification && !notification.isRead) {
                    notification.isRead = true;
                    state.unreadCount = Math.max(0, state.unreadCount - 1);
                }
            })
            // Get unread count
            .addCase(getUnreadCountThunk.fulfilled, (state, action) => {
                state.unreadCount = action.payload;
            })
            // Mark all as read
            .addCase(markAllNotificationsAsReadThunk.pending, (state) => {
                state.status = 'loading';
            })
            .addCase(markAllNotificationsAsReadThunk.fulfilled, (state, action) => {
                state.notifications.forEach(notification => {
                    notification.isRead = true;
                });
                state.unreadCount = 0;
                state.status = 'succeeded';
            })
            .addCase(markAllNotificationsAsReadThunk.rejected, (state, action) => {
                state.status = 'failed';
                state.error = action.payload || 'Failed to mark all notifications as read';
            });
    },
});

export const { clearNotifications } = notificationsSlice.actions;

export const selectNotifications = (state) => state.notifications.notifications;
export const selectNotificationsStatus = (state) => state.notifications.status;
export const selectUnreadCount = (state) => state.notifications.unreadCount;
export const selectHasMoreNotifications = (state) => state.notifications.hasMore;
export const selectLastDoc = (state) => state.notifications.lastDoc;

export default notificationsSlice.reducer; 